// The source registry.
//
// Sources are configuration, not code — everything on this page is a
// row edit, never a deploy. The column that matters most is the
// permission verdict: a source we have never audited from Cloudflare's
// network shows as "unaudited" and is skipped by retrieval, because
// "we haven't checked" is not consent.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Radio, ShieldCheck, ShieldOff, ShieldQuestion, RefreshCw } from 'lucide-react';
import { sourcesApi, ApiError } from '../lib/api';
import type { SourceListItem } from '@shared/types';

const CATEGORY_LABELS: Record<string, string> = {
  municipal: 'Municipal & civic',
  institution: 'Institutions & events',
  outlet: 'Regional outlets',
  service: 'Service data',
};
const CATEGORY_ORDER = ['municipal', 'institution', 'outlet', 'service'];

export default function SourcesListPage() {
  const [sources, setSources] = useState<SourceListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [auditing, setAuditing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setErr(null);
      const { sources } = await sourcesApi.list();
      setSources(sources);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function runAudit() {
    if (
      !confirm(
        'Re-check robots.txt for every source from Cloudflare’s network? This may enable or disable sources.',
      )
    )
      return;
    setAuditing(true);
    try {
      await sourcesApi.runAudit();
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed');
    } finally {
      setAuditing(false);
    }
  }

  const unaudited = sources?.filter((s) => s.crawl_allowed === null).length ?? 0;

  return (
    <div>
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Radio size={18} className="text-slate-600" />
            <h2 className="text-lg font-medium text-slate-900">Sources</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            The retrieval registry. Independence groups are what the corroboration gate counts —
            two co-owned stations running the same wire copy are one group, not two.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button className="btn-secondary" onClick={runAudit} disabled={auditing}>
            <RefreshCw size={14} className={auditing ? 'animate-spin' : ''} />
            {auditing ? 'Auditing…' : 'Run audit'}
          </button>
          <button className="btn-primary" onClick={() => navigate('/settings/sources/new')}>
            <Plus size={14} /> New source
          </button>
        </div>
      </header>

      {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

      {unaudited > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-900">
            {unaudited} source{unaudited === 1 ? ' has' : 's have'} never been audited from
            Cloudflare&rsquo;s network. Retrieval skips {unaudited === 1 ? 'it' : 'them'} until the
            permission audit runs.
          </p>
        </div>
      )}

      {sources === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <div className="space-y-6">
          {CATEGORY_ORDER.filter((c) => sources.some((s) => s.category === c)).map((cat) => (
            <div key={cat}>
              <h3 className="text-sm font-medium text-slate-900 mb-2">
                {CATEGORY_LABELS[cat] ?? cat}
              </h3>
              <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                {sources
                  .filter((s) => s.category === cat)
                  .map((s) => (
                    <Row key={s.id} source={s} />
                  ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PermissionBadge({ source }: { source: SourceListItem }) {
  if (source.crawl_allowed === null) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded"
        title="Never audited from Cloudflare's network — retrieval will skip this source"
      >
        <ShieldQuestion size={10} /> unaudited
      </span>
    );
  }
  if (source.crawl_allowed) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
        <ShieldCheck size={10} /> crawl ok
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
      <ShieldOff size={10} /> crawl blocked
    </span>
  );
}

function Row({ source }: { source: SourceListItem }) {
  const navigate = useNavigate();

  return (
    <li>
      <button
        type="button"
        onClick={() => navigate(`/settings/sources/${source.id}`)}
        className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-slate-50"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-sm font-medium truncate ${
                source.enabled ? 'text-slate-900' : 'text-slate-400 line-through'
              }`}
            >
              {source.name}
            </span>
            {source.town && <span className="text-xs text-slate-500">{source.town}</span>}
            <PermissionBadge source={source} />
            {source.claude_fetch_allowed === false && (
              <span
                className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded"
                title="Blocks Anthropic's crawlers — corroboration text must come via /crawl"
              >
                no claude fetch
              </span>
            )}
            {!source.enabled && (
              <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                {source.disabled_by === 'human' ? 'off (manual)' : 'off (auditor)'}
              </span>
            )}
            {source.alert_state !== 'ok' && (
              <span
                className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                  source.alert_state === 'failing'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-amber-100 text-amber-800'
                }`}
              >
                {source.alert_state}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-mono">{source.slug}</span>
            <span>·</span>
            <span>{source.retrieval_method}</span>
            <span>·</span>
            <span title="Independence group — corroboration counts groups, not domains">
              group: {source.independence_group}
            </span>
            {source.last_doc_count !== null && (
              <>
                <span>·</span>
                <span>{source.last_doc_count} docs last run</span>
              </>
            )}
          </div>
          {source.disabled_reason && (
            <p className="text-xs text-slate-400 mt-1">{source.disabled_reason}</p>
          )}
        </div>
      </button>
    </li>
  );
}
